import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { BidRoundService } from '../../../../services/bid-round.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { ToastService } from '../../../../services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import { BaseComponent } from 'src/app/shared/base.component';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminComponent extends BaseComponent implements OnInit {

  bidRoundForm: FormGroup;
  searchForm: FormGroup;
  userForm: FormGroup;
  bidStatusOptions = ['OPEN', 'CREATED', 'CLOSED'];
  userGroupList = ['Admin', 'Consumer', 'Prosumer', 'Guest', 'Pending']
  bidTypeOptions = ['Monthly', 'Day ahead'];
  isEdit = false;
  bidRoundId: string | null = null;
  bidRounds: any[] = [];
  errorMessage = '';
  showForm = false;
  minDate: string;
  completedBidRound: any[] = [];
  tradedBidRound: any[] = [];
  userList: any[] = [];
  showUserForm = false;
  user_id: any;

  totalItems = 0;
  itemsPerPage = 10;
  currentPage = 1;

  totalSearchItems = 0;
  searchItemsPerPage = 10;
  searchCurrentPage = 1;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private userService: UserService,
    private toastService: ToastService,
    private bidRoundService: BidRoundService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    super();
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    this.minDate = yesterday.toISOString().slice(0, 16);

    this.bidRoundForm = this.fb.group({
      start_time: ["", Validators.required],
      end_time: ["", [Validators.required]],
      bid_type: ["", Validators.required],
      start_trade_period: ["", [Validators.required]],
      end_trade_period: ["", [Validators.required]],
      status: [""],
    });

    this.userForm = this.fb.group({
      group_id: ["", Validators.required]
    });

    this.searchForm = this.fb.group({
      bid_status: [""]
    });
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.bidRoundId = params['id'];
      if (this.bidRoundId) {
        this.isEdit = true;
        this.loadBidRound(this.bidRoundId);
        this.showForm = true;
      }
      this.cdr.markForCheck();
    });

    this.searchForm.patchValue({ bid_status: "OPEN" });
    this.onSearch(this.currentPage);
    this.getCompletedAndTradedBidRound(this.currentPage);
    this.getUsers();
  }

  loadBidRound(id: string): void {
    this.bidRoundService.getBidRound(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.bidRoundForm.patchValue(data);
        this.cdr.markForCheck();
      });
  }

  onSubmit(): void {
    if (this.bidRoundForm.invalid) return;

    const formData = { ...this.bidRoundForm.value };
    ['start_time', 'end_time', 'start_trade_period', 'end_trade_period'].forEach(field => {
      if (formData[field]) {
        formData[field] = this.formatDateForBackend(new Date(formData[field]));
      }
    });

    const request = (this.isEdit && this.bidRoundId)
      ? this.bidRoundService.updateBidRound(this.bidRoundId, formData)
      : this.bidRoundService.createBidRound(formData);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.onSearch(this.currentPage);
        this.showForm = false;
        this.cdr.markForCheck();
      },
      error: (err) => console.error("Error saving bid round:", err)
    });
  }

  private formatDateForBackend(date: Date): string {
    return date.getFullYear().toString().padStart(4, '0') + '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') + '-' +
      date.getDate().toString().padStart(2, '0') + ' ' +
      date.getHours().toString().padStart(2, '0') + ':' +
      date.getMinutes().toString().padStart(2, '0');
  }

  onDelete(bidRoundId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Confirmation',
        message: 'Are you sure you want to delete this bid round?'
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.bidRoundService.deleteBidRound(bidRoundId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.onSearch(this.currentPage),
            error: (err) => console.error("Error deleting bid round:", err)
          });
      }
    });
  }

  onCancel(): void {
    this.showForm = false;
    this.showUserForm = false;
    this.cdr.markForCheck();
  }

  onSearch(page: number): void {
    const offset = (page - 1) * this.searchItemsPerPage;
    const filters = {
      'bid_status': this.searchForm.value.bid_status,
      'limit': this.searchItemsPerPage,
      'offset': offset
    };
    this.bidRoundService.searchBidRounds(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.bidRounds = data.items;
        this.totalSearchItems = data.total_items;
        this.searchCurrentPage = page;
        this.showForm = false;
        this.cdr.markForCheck();
      });
  }

  getCompletedAndTradedBidRound(page: number): void {
    const offset = (page - 1) * this.itemsPerPage;
    const param = {
      'bid_status': 'COMPLETED,TRADED',
      'limit': this.itemsPerPage,
      'offset': offset
    };

    this.bidRoundService.searchBidRounds(param)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.completedBidRound = data.items;
        this.totalItems = data.total_items;
        this.currentPage = page;
        this.cdr.markForCheck();
      });
  }

  onPageChanged(page: number): void {
    this.getCompletedAndTradedBidRound(page);
  }

  onSearchChanged(page: number): void {
    this.onSearch(page);
  }

  onEdit(bidRound: any): void {
    this.bidRoundId = bidRound.id;
    this.isEdit = true;
    this.showForm = true;
    this.bidRoundForm.patchValue(bidRound);
    this.cdr.markForCheck();
  }

  onNew(): void {
    this.isEdit = false;
    this.showForm = true;
    this.bidRoundForm.reset();
    this.prepopulateDates();
    this.bidRoundForm.patchValue({ bid_type: 'Day ahead' });
    this.cdr.markForCheck();
  }

  private prepopulateDates(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const start_time = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0);
    const end_time = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 18, 0);
    const start_trade_period = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate() + 1, 0, 0);
    const end_trade_period = new Date(start_trade_period.getFullYear(), start_trade_period.getMonth(), start_trade_period.getDate() + 1);

    this.bidRoundForm.patchValue({
      start_time: this.formatDateForInput(start_time),
      end_time: this.formatDateForInput(end_time),
      start_trade_period: this.formatDateForInput(start_trade_period),
      end_trade_period: this.formatDateForInput(end_trade_period),
    });
  }

  private formatDateForInput(date: Date): string {
    return date.getFullYear().toString().padStart(4, '0') + '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') + '-' +
      date.getDate().toString().padStart(2, '0') + 'T' +
      date.getHours().toString().padStart(2, '0') + ':' +
      date.getMinutes().toString().padStart(2, '0');
  }

  getUsers(): void {
    this.userService.getAllUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.userList = data.items;
        this.cdr.markForCheck();
      });
  }

  editUser(user_id: any): void {
    this.showUserForm = true;
    this.user_id = user_id;
    this.cdr.markForCheck();
  }

  userSubmit(): void {
    const body = {
      'user_id': this.user_id,
      'group_id': this.userForm.value.group_id
    };
    this.userService.addUserToGroup(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show('User group updated successfully!');
          this.getUsers();
        },
        error: (err: any) => console.error("Error updating user group:", err)
      });
    this.showUserForm = false;
    this.cdr.markForCheck();
  }

  deleteUser(user_id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Confirmation',
        message: 'Are you sure you want to delete this user?'
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.userService.deleteUser(user_id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastService.show('User deleted successfully!');
              this.getUsers();
            },
            error: (err: any) => console.error("Error deleting user:", err)
          });
      }
    });
  }
}
